import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { falBars } from 'shared/@generated/icons/falBars';
import { falEnvelope } from 'shared/@generated/icons/falEnvelope';
import { falUserCircle } from 'shared/@generated/icons/falUserCircle';
import { Pressable, TouchableOpacity, View } from 'react-native';
import { Icon } from 'shared/components/icon';
import { Link } from 'shared/components/link';
import logo from 'shared/assets/logo.svg';
import { useTranslation } from 'react-i18next';
import { LocaleSwitcher } from 'shared/components/locale-switcher';
import { Text } from 'shared/components/text';
import { useUserStore } from 'shared/state/user';
import { Avatar } from 'shared/components/avatar';
import { LoginButton } from 'shared/components/login-button';
import { useScrolled } from 'shared/library/use-scrolled';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { useEffect, useRef } from 'react';
import { BaseModalRef } from 'shared/components/base-modal';
import { UnreadCount } from 'shared/components/chat/unread-count';
import { SvgImage } from 'shared/components/svg-image';
import { formatAppVersion } from 'shared/library/app-version';
import { useRoute, useIsCurrentRoute, RouteName } from 'shared/library/routes/route';
import dynamic from 'next/dynamic';
import { GlobalSearch } from './global-search';
import { flags } from 'shared/library/i18n/flags';
import { languages } from 'shared/library/i18n/languages';
import { useLocale } from 'shared/library/i18n/use-locale';

const AccountDropdownMenu = dynamic(() =>
  import('shared/components/account-dropdown-menu').then((mod) => mod.AccountDropdownMenu),
);

type MenuItem = {
  title: string;
  routeName: RouteName;
  desktop?: boolean;
  mobile?: boolean;
};

const Menu = () => {
  const { t } = useTranslation('web');
  const user = useUserStore((state) => state.user);
  const { scrolled } = useScrolled();
  const mobileMenuRef = useRef<BaseModalRef>(null);
  const route = useRoute();
  const isCurrentRoute = useIsCurrentRoute();
  const { languageCode } = useLocale();

  // The menu's language row switches locale without a page load, so close the menu after it.
  useEffect(() => {
    mobileMenuRef.current?.close();
  }, [languageCode]);

  const menuItems: MenuItem[] = [
    {
      title: t('layout.menu.browse'),
      routeName: 'listings.index',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.sell'),
      routeName: user ? 'me.listings.index' : 'selling-a-pet',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.breeders'),
      routeName: 'breeders.index',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.breeds'),
      routeName: 'breeds.index',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.shop'),
      routeName: 'shop.home',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.blog'),
      routeName: 'blog.index',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.help'),
      routeName: 'help.index',
      desktop: true,
      mobile: true,
    },
    {
      title: t('layout.menu.about_us'),
      routeName: 'about',
      mobile: true,
    },
    {
      title: t('layout.menu.testimonials'),
      routeName: 'testimonials',
      mobile: true,
    },
  ];

  return (
    <>
      <div className={'h-[var(--header-h)]'} />

      <nav
        className={`fixed top-0 z-50 h-[var(--header-h)] w-full justify-center border-b px-3 transition-colors duration-75 ${scrolled ? 'border-brand-brown-dark/10 bg-brand-cream-light' : 'border-transparent bg-transparent'}`}
      >
        <div className={'mx-auto flex h-full w-full max-w-screen-xl items-center justify-between'}>
          <div className={'flex items-center gap-1 sm:gap-3'}>
            <FullscreenModal
              ref={mobileMenuRef}
              headerTitle={t('layout.menu.title')}
              trigger={(modal) => (
                <TouchableOpacity
                  aria-label={t('layout.menu.title')}
                  className={'rounded p-2 md:hidden'}
                  onPress={modal.open}
                  testID="mobile-menu-open"
                >
                  <Icon icon={falBars} className={'text-brand-brown'} />
                </TouchableOpacity>
              )}
            >
              <View className={'-m-3'}>
                {menuItems
                  .filter((i) => i.mobile)
                  .map((item, i) => (
                    <Link
                      key={i}
                      href={route(item.routeName)}
                      containerClassName={`border-brand-brown-dark/10 w-full border-b px-4 py-3 ${isCurrentRoute(item.routeName) ? 'bg-brand-cream' : ''}`}
                      textClassName={'text-brand-brown-dark'}
                      onPress={() => mobileMenuRef.current?.close()}
                    >
                      {item.title}
                    </Link>
                  ))}

                <MobileLanguageRow />
              </View>

              <MenuFooter />
            </FullscreenModal>

            <Link href={route('home')} asBlock>
              <SvgImage src={logo} alt={'Get a Pet Logo'} containerClassName={'aspect-logo h-8'} />
            </Link>
          </div>

          <div className={'hidden flex-grow items-center pl-3 md:flex'}>
            {menuItems
              .filter((i) => i.desktop)
              .map((item, i) => (
                <Link
                  key={i}
                  href={route(item.routeName)}
                  textClassName={`px-3 py-2 text-sm md:text-base ${isCurrentRoute(item.routeName) ? 'text-brand-brown-dark underline' : 'text-brand-brown'}`}
                >
                  {item.title}
                </Link>
              ))}
          </div>

          <div className={'flex flex-shrink items-center gap-1 sm:gap-3'}>
            {user ? (
              <>
                <GlobalSearch />

                <Link
                  href={route('me.inbox')}
                  containerClassName={'hidden items-center px-1.5 md:flex'}
                  asBlock
                >
                  <View className={'items-center'}>
                    <Icon icon={falEnvelope} className={'text-brand-brown-dark size-6 p-[3px]'} />
                    <Text
                      className={`text-brand-brown-dark text-2xs group-hover:underline md:text-xs ${isCurrentRoute('me.inbox') ? 'underline' : ''}`}
                    >
                      {t('layout.menu.inbox')}
                    </Text>

                    <UnreadCount className={'absolute -right-1 -top-1'} />
                  </View>
                </Link>

                <AccountDropdownMenu>
                  <Pressable className={'group hidden items-center overflow-hidden px-1.5 md:flex'}>
                    {user.avatar ? (
                      <Avatar
                        model={user}
                        containerClassName={'size-6'}
                        imageClassName={'rounded-full'}
                      />
                    ) : (
                      <Icon
                        icon={falUserCircle}
                        className={'text-brand-brown-dark size-6 p-[3px]'}
                      />
                    )}
                    <Text
                      className={`text-brand-brown-dark text-2xs w-full truncate text-center group-hover:underline md:text-xs ${isCurrentRoute('me.index') ? 'underline' : ''}`}
                    >
                      {user.first_name ?? t('layout.menu.account')}
                    </Text>
                  </Pressable>
                </AccountDropdownMenu>

                <div className={'hidden pl-1.5 md:block'}>
                  <LocaleSwitcher />
                </div>
              </>
            ) : (
              <>
                <GlobalSearch />

                <div className={'hidden md:block'}>
                  <LocaleSwitcher />
                </div>

                <LoginButton dense={true} />
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

// On mobile the header has no room for the language switcher, so it lives in the menu.
const MobileLanguageRow = () => {
  const { t } = useTranslation('web');
  const { languageCode } = useLocale();

  return (
    <LocaleSwitcher
      trigger={
        <View
          className={
            'border-brand-brown-dark/10 group w-full cursor-pointer flex-row items-center border-b px-4 py-3'
          }
        >
          <Text className={'text-brand-brown-dark flex-1'}>{t('layout.menu.language')}</Text>
          <SvgImage
            src={flags[languageCode]}
            alt={languageCode}
            containerClassName={'mr-2 size-5'}
          />
          <Text className={'text-brand-brown-dark/70 group-hover:text-brand-brown-dark mr-1.5'}>
            {languages[languageCode]}
          </Text>
          <Icon icon={falAngleDown} className={'text-brand-brown-dark/70 size-4'} />
        </View>
      }
    />
  );
};

const MenuFooter = () => {
  return (
    <>
      <SvgImage src={logo} alt={'Get a Pet Logo'} containerClassName={'aspect-logo mt-12 h-10'} />

      <View className={'mt-3 flex-1 items-center'}>
        <Text className="text-brand-brown-dark text-sm font-semibold">
          © Copyright {new Date().getFullYear()} Get a Pet™
        </Text>
        <Text className="text-brand-brown-dark text-sm">Dogs Unleashed AB (559049-6807)</Text>
        <Text className="text-brand-brown-dark text-sm">{formatAppVersion()}</Text>
      </View>
    </>
  );
};

export { Menu, MenuFooter };
